// T2: Active call mobile experience.
//
// Covers Stitch v2 design `active_intercom_call_mobile_experience` —
// 375px-first layout, big "End call" button, snapshot reel. Designed
// for operators answering from a phone while away from a desk.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import { useIntercomCall, useIntercomCalls, statusLabel, statusTone } from '../lib/intercomCalls.js';
import { useBranding } from '../lib/branding.js';
import { ApiError } from '../lib/api.js';

function formatDuration(startIso: string | null): string {
  if (!startIso) return '0:00';
  const s = Math.max(0, Math.floor((Date.now() - new Date(startIso).getTime()) / 1000));
  const m = Math.floor(s / 60);
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

export function ActiveIntercomCallPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { call, error, loading } = useIntercomCall(id);
  const { end } = useIntercomCalls();
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);
  void tick; // tick exists only to trigger re-render for the duration counter

  if (loading || !id) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !call) {
    return (
      <p role="alert" className="text-error">
        <Icon name="error" /> {error ?? 'Call not found'}
      </p>
    );
  }

  async function onEnd() {
    if (!id) return;
    setBusy(true);
    setActionErr(null);
    try {
      await end(id, { reason: 'operator_ended' });
      navigate('/intercom/ops');
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'Could not end call');
    } finally {
      setBusy(false);
    }
  }

  const brandColor = branding.brand_color ?? undefined;
  const isActive = call.status === 'answered';

  return (
    <div className="max-w-sm mx-auto space-y-4 py-4">
      <div className="flex items-center justify-between">
        <Badge tone={statusTone(call.status)}>{statusLabel(call.status)}</Badge>
        <span className="font-mono text-lg" aria-live="polite">
          {isActive ? formatDuration(call.answered_at) : '—'}
        </span>
      </div>

      <Card>
        <CardBody>
          <div className="text-center">
            <p
              className="text-2xl font-semibold"
              style={brandColor ? { color: brandColor } : undefined}
            >
              Callbox {call.callbox_id.slice(0, 8)}
            </p>
            <p className="text-sm text-on-surface-variant mt-1">
              Started {new Date(call.initiated_at).toLocaleTimeString()}
            </p>
          </div>

          {call.snapshot_ids.length > 0 ? (
            <div className="mt-4 grid grid-cols-3 gap-2">
              {call.snapshot_ids.slice(0, 3).map((sid) => (
                <img
                  key={sid}
                  src={`/api/v1/snapshots/${sid}/image`}
                  alt="Callbox snapshot"
                  className="aspect-square object-cover rounded border border-outline-variant"
                />
              ))}
            </div>
          ) : (
            <div className="mt-4 aspect-video bg-surface-container-high rounded flex items-center justify-center text-on-surface-variant">
              <Icon name="image" />
            </div>
          )}
        </CardBody>
      </Card>

      {actionErr ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {actionErr}
        </p>
      ) : null}

      {isActive ? (
        <Button
          variant="danger"
          onClick={onEnd}
          disabled={busy}
          className="w-full h-14 text-lg"
        >
          <Icon name="call_end" /> {busy ? 'Ending…' : 'End call'}
        </Button>
      ) : (
        <Button
          variant="ghost"
          onClick={() => navigate('/intercom/ops')}
          className="w-full"
        >
          Back to operations
        </Button>
      )}
    </div>
  );
}
